/**
 * Creates a throttled function that only invokes the provided function at most once per every `limit` milliseconds.
 *
 * @param func The function to throttle
 * @param limit The number of milliseconds to throttle invocations to
 * @returns A new, throttled, function
 */

export function throttle<T extends (...args: any[]) => any>(func: T, limit: number): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;
  return function (this: any, ...args: Parameters<T>): void {
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): {
  (...args: Parameters<T>): void;
  cancel: () => void;
} {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  function debounced(this: any, ...args: Parameters<T>) {
    const context = this;

    if (timeout !== null) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      func.apply(context, args);
    }, wait);
  }

  debounced.cancel = () => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }
  };

  return debounced;
}

export function isColorSchemeDetectionSupported() {
  // Check if matchMedia and the prefers-color-scheme query are supported
  // if browser does not support matchMedia it won't raise an error, it should return 'not all'
  return window.matchMedia && window.matchMedia('(prefers-color-scheme)').media !== 'not all';
}

export function isDarkModePreferred() {
  return isColorSchemeDetectionSupported() && window.matchMedia('(prefers-color-scheme: dark)').matches;
}
